import { useRef, useState, type FormEvent } from 'react';
import { api, type AccountState } from '../api.ts';
import { GeneratedPassword } from './GeneratedPassword.tsx';
import { PasswordEntry } from './PasswordEntry.tsx';
import { message } from './Platform.tsx';

interface CreateOwnerProps {
  readonly accountId: string;
  readonly accountState: AccountState;
  /** Called after a successful create, so the screen can reload its operators. */
  readonly onCreated: () => void;
}

interface Revealed {
  readonly name: string;
  readonly password: string;
}

/**
 * Give a client an owner.
 *
 * A new account has no operators, and until this form existed the only way to
 * give it one was `POST /users` from a terminal. The role is not offered: the
 * route creates owners only, and everybody else is that owner's to invite.
 *
 * The typed password is cleared the moment it has been accepted and moves into
 * `revealed`, which lives exactly as long as the one-time panel does.
 */
export const CreateOwner = ({ accountId, accountState, onCreated }: CreateOwnerProps) => {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  const blocked = accountState === 'suspended' || accountState === 'closed';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    // Enter pressed twice is a second submit however the button looks, and a
    // second create here is a 409 shown over the owner the first one made.
    if (busy) return;
    setBusy(true);
    setError(null);
    setAnnouncement('');
    try {
      const { user } = await api.createPlatformOwner(accountId, {
        email: email.trim(),
        displayName: displayName.trim(),
        password,
      });
      setRevealed({ name: user.displayName, password });
      setEmail('');
      setDisplayName('');
      setPassword('');
      setAnnouncement(`${user.displayName} is now an owner of this client. Their password is shown once, below.`);
      onCreated();
    } catch (cause) {
      setError(message(cause));
    } finally {
      setBusy(false);
    }
  };

  // The panel held focus, and unmounting it would drop a keyboard user at the
  // top of the document. Back to the form it came from, ready for the next one.
  const dismiss = () => {
    setRevealed(null);
    firstFieldRef.current?.focus();
  };

  return (
    <form className="pf-owner" onSubmit={(event) => void submit(event)}>
      <h3>Create an owner</h3>
      <p className="lead">
        An owner can sign in to this client’s console and invite the rest of its people. Set their
        password here and pass it on yourself — DriftWatch sends no email.
      </p>

      <p className="sr-only" role="status" aria-live="polite">
        {announcement}
      </p>
      {revealed !== null && (
        <GeneratedPassword
          name={revealed.name}
          password={revealed.password}
          onDismiss={dismiss}
        />
      )}
      {error !== null && (
        <p className="error-banner" role="alert">
          {error}
        </p>
      )}
      {blocked && (
        <p className="callout callout-warn">
          This client is {accountState}. You can create the owner now, but they will not be able to
          sign in until the client’s state is changed back.
        </p>
      )}

      <div className="td-fields">
        <div className="field">
          <label htmlFor="pf-owner-name">Name</label>
          <input
            id="pf-owner-name"
            ref={firstFieldRef}
            autoComplete="off"
            value={displayName}
            disabled={busy}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="pf-owner-email">Email</label>
          <input
            id="pf-owner-email"
            type="email"
            autoComplete="off"
            spellCheck={false}
            value={email}
            disabled={busy}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <PasswordEntry
          id="pf-owner-password"
          label="Password"
          value={password}
          disabled={busy}
          onChange={setPassword}
        />
      </div>
      <div className="toolbar">
        <button
          type="submit"
          className="btn btn-primary"
          aria-busy={busy || undefined}
          disabled={busy || email.trim() === '' || displayName.trim() === '' || password === ''}
        >
          {busy ? 'Creating…' : 'Create owner'}
        </button>
      </div>
    </form>
  );
};
